import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/AdminLayout';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Grid,
  Snackbar,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
} from '@mui/material';
import { 
  Delete, 
  Search, 
  Refresh, 
  Email, 
  Phone, 
  Person, 
  Block, 
  CheckCircle, 
  Add, 
  Edit,
  Visibility,
  VisibilityOff,
  ShoppingCart,
  Euro,
  TrendingUp,
  AccessTime,
} from '@mui/icons-material';
import { 
  collection, 
  getDocs, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  orderBy, 
  limit, 
  Timestamp,
  addDoc,
  updateDoc,
  getDoc,
  onSnapshot,
  QuerySnapshot,
  DocumentData,
} from '../../services/firestoreCompat';
import { db, auth } from '../../services/firebaseConfig';
import { createUserWithEmailAndPassword } from 'firebase/auth';

interface Customer {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  phone: string;
  created_at: Timestamp | null;
  updated_at: Timestamp | null;
  profile_image_url?: string;
  fcm_token?: string;
  is_active?: boolean;
  role?: string;
  created_by?: string; // 'self' for self-registered, 'admin' for admin-created
  source?: string; // 'otp', 'webshop', 'email', etc.
  street?: string;
  postcode?: string;
  city?: string;
  total_orders?: number;
  total_spent?: number;
  last_order_date?: Timestamp | null;
}

const CustomersPage: React.FC = () => {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [ordersData, setOrdersData] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    is_active: true,
  });

  // Fetch orders data for statistics
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const ordersRef = collection(db, 'orders');
        let q;
        try {
          q = query(ordersRef, orderBy('created_at', 'desc'));
        } catch (e) {
          q = query(ordersRef);
        }
        const snapshot = await getDocs(q);
        const orders: any[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          orders.push({
            id: doc.id,
            customer_email: data.customer_email || data.customer?.email || '',
            customer_id: data.customer_id || data.customer?.id || '',
            customer_uid: data.customer_uid || '',
            customer_phone: data.customer_phone || '',
            total: data.total || 0,
            created_at: data.created_at || null,
          });
        });
        setOrdersData(orders);
        console.log(`✅ Loaded ${orders.length} orders for customer statistics`);
      } catch (e) {
        console.error('❌ Error fetching orders:', e);
      }
    };
    fetchOrders();
  }, []);

  // Fetch customers from Firestore with real-time listener
  useEffect(() => {
    if (ordersData.length === 0 && !loading) {
      // Wait for orders to load first
      return;
    }

    setLoading(true);
    setError(null);

    // Set up real-time listener for immediate updates when customers register
    const customersRef = collection(db, 'customers');
    let q;
    
    try {
      q = query(customersRef, orderBy('created_at', 'desc'));
    } catch (e) {
      // Fallback if orderBy fails (no index)
      q = query(customersRef);
    }

    // Real-time listener - automatically updates when new customers register
    console.log('🔍 [ADMIN] Setting up real-time listener for customers collection...');
    const unsubscribe = onSnapshot(
      q,
      (snapshot: QuerySnapshot<DocumentData>) => {
        console.log(`📊 [ADMIN] Customer snapshot received: ${snapshot.size} customers`);
        const customersData: Customer[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          const customerId = doc.id;
          const customerEmail = data.email || '';
          const customerPhone = data.phone || '';

          // Calculate order statistics — match by email, uid, OR phone number
          const customerOrders = ordersData.filter(order =>
            (customerEmail && order.customer_email?.toLowerCase() === customerEmail.toLowerCase()) ||
            order.customer_id === customerId ||
            order.customer_uid === customerId ||
            (customerPhone && order.customer_phone === customerPhone)
          );
          const totalOrders = customerOrders.length;
          const totalSpent = customerOrders.reduce((sum, order) => {
            const orderTotal = typeof order.total === 'number' 
              ? order.total 
              : parseFloat(order.total || '0');
            return sum + orderTotal;
          }, 0);
          
          // Get last order date
          const lastOrder = customerOrders.length > 0 
            ? customerOrders.sort((a, b) => {
                const aTime = a.created_at?.toMillis?.() || 0;
                const bTime = b.created_at?.toMillis?.() || 0;
                return bTime - aTime;
              })[0]
            : null;
          const lastOrderDate = lastOrder?.created_at || null;
          
          const profileImageUrl = data.profile_image_url;
          
          // Debug logging for profile images
          if (profileImageUrl) {
            console.log(`🖼️ [ADMIN] Customer ${customerId} has profile image:`, profileImageUrl);
          } else {
            console.log(`⚠️ [ADMIN] Customer ${customerId} (${data.full_name || customerEmail}) has no profile_image_url`);
          }
          
          customersData.push({
            id: customerId,
            email: customerEmail,
            first_name: data.first_name || '',
            last_name: data.last_name || '',
            full_name: data.full_name || data.name || `${data.first_name || ''} ${data.last_name || ''}`.trim() || 'No Name',
            phone: data.phone || '',
            created_at: data.created_at || null,
            updated_at: data.updated_at || null,
            profile_image_url: profileImageUrl,
            fcm_token: data.fcm_token,
            is_active: data.is_active !== false,
            role: data.role || 'customer',
            created_by: data.created_by || 'self',
            source: data.source || (data.email ? 'email' : 'unknown'),
            street: data.street || '',
            postcode: data.postcode || '',
            city: data.city || '',
            total_orders: totalOrders,
            total_spent: totalSpent,
            last_order_date: lastOrderDate,
          });
        });

        // Sort client-side if orderBy failed
        if (customersData.length > 0 && !customersData[0]?.created_at) {
          customersData.sort((a, b) => {
            const aName = a.full_name.toLowerCase();
            const bName = b.full_name.toLowerCase();
            return aName.localeCompare(bName);
          });
        } else if (customersData.length > 0) {
          // Sort by created_at descending (newest first)
          customersData.sort((a, b) => {
            if (!a.created_at || !b.created_at) return 0;
            try {
              const toMs = (v: any): number => {
                if (!v) return 0;
                if (typeof v === 'string') return new Date(v).getTime();
                if (typeof v.toMillis === 'function') return v.toMillis();
                if (v instanceof Date) return v.getTime();
                return 0;
              };
              return toMs(b.created_at) - toMs(a.created_at); // Descending
            } catch (e) {
              return 0;
            }
          });
        }

        setCustomers(customersData);
        setLoading(false);
        
        // Debug: Count customers with profile images
        const withImages = customersData.filter(c => c.profile_image_url).length;
        console.log(`✅ [ADMIN] Real-time update: Loaded ${customersData.length} customers with order statistics`);
        console.log(`   📸 ${withImages} customers have profile images`);
        console.log(`   ⚠️ ${customersData.length - withImages} customers missing profile images`);
        
        // Log first few profile image URLs for debugging
        customersData.slice(0, 5).forEach(customer => {
          if (customer.profile_image_url) {
            console.log(`   🖼️ [${customer.full_name}]: ${customer.profile_image_url.substring(0, 50)}...`);
          }
        });
      },
      (error: any) => {
        console.error('❌ Real-time listener error:', error);
        setError(error.message || 'Failed to load customers');
        setLoading(false);
        // Fallback to one-time fetch
        fetchCustomers();
      }
    );
    
    return () => {
      unsubscribe(); // Cleanup listener on unmount
    };
  }, [ordersData]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      setError(null);

      const customersRef = collection(db, 'customers');
      let q;
      
      try {
        q = query(customersRef, orderBy('created_at', 'desc'));
      } catch (e) {
        // Fallback if orderBy fails (no index)
        q = query(customersRef);
      }
      
      const snapshot = await getDocs(q);

      const customersData: Customer[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        const customerId = doc.id;
        const customerEmail = data.email || '';
        const customerPhone = data.phone || '';

        const customerOrders = ordersData.filter(order =>
          (customerEmail && order.customer_email?.toLowerCase() === customerEmail.toLowerCase()) ||
          order.customer_id === customerId ||
          order.customer_uid === customerId ||
          (customerPhone && order.customer_phone === customerPhone)
        );
        const totalOrders = customerOrders.length;
        const totalSpent = customerOrders.reduce((sum, order) => {
          const orderTotal = typeof order.total === 'number'
            ? order.total
            : parseFloat(order.total || '0');
          return sum + orderTotal;
        }, 0);

        const lastOrder = customerOrders.length > 0
          ? customerOrders.sort((a, b) => {
              const aTime = a.created_at?.toMillis?.() || 0;
              const bTime = b.created_at?.toMillis?.() || 0;
              return bTime - aTime;
            })[0]
          : null;
        const lastOrderDate = lastOrder?.created_at || null;

        customersData.push({
          id: customerId,
          email: customerEmail,
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          full_name: data.full_name || data.name || `${data.first_name || ''} ${data.last_name || ''}`.trim() || 'No Name',
          phone: data.phone || '',
          created_at: data.created_at || null,
          updated_at: data.updated_at || null,
          profile_image_url: data.profile_image_url,
          fcm_token: data.fcm_token,
          is_active: data.is_active !== false,
          role: data.role || 'customer',
          created_by: data.created_by || 'self',
          source: data.source || (data.email ? 'email' : 'unknown'),
          street: data.street || '',
          postcode: data.postcode || '',
          city: data.city || '',
          total_orders: totalOrders,
          total_spent: totalSpent,
          last_order_date: lastOrderDate,
        });
      });

      // Sort client-side if orderBy failed
      if (!customersData[0]?.created_at) {
        customersData.sort((a, b) => {
          const aName = a.full_name.toLowerCase();
          const bName = b.full_name.toLowerCase();
          return aName.localeCompare(bName);
        });
      }

      setCustomers(customersData);
      setLoading(false);
      console.log(`✅ Loaded ${customersData.length} customers`);
    } catch (e: any) {
      console.error('❌ Error fetching customers:', e);
      setError(e.message || 'Failed to fetch customers');
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setSelectedCustomer(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
      is_active: true,
    });
    setEditDialogOpen(true);
  };

  const handleEditClick = (customer: Customer) => {
    setSelectedCustomer(customer);
    setFormData({
      first_name: customer.first_name,
      last_name: customer.last_name,
      email: customer.email,
      phone: customer.phone,
      password: '',
      confirmPassword: '',
      is_active: customer.is_active !== false,
    });
    setEditDialogOpen(true);
  };

  const handleSaveCustomer = async () => {
    try {
      setSaving(true);

      // Validate form
      if (!formData.first_name.trim() || !formData.last_name.trim()) {
        setSnackbar({
          open: true,
          message: 'First name and last name are required',
          severity: 'error',
        });
        return;
      }

      if (!formData.email.trim()) {
        setSnackbar({
          open: true,
          message: 'Email is required',
          severity: 'error',
        });
        return;
      }

      if (selectedCustomer) {
        // Update existing customer
        const customerRef = doc(db, 'customers', selectedCustomer.id);
        
        const updateData: any = {
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          full_name: `${formData.first_name.trim()} ${formData.last_name.trim()}`,
          email: formData.email.trim().toLowerCase(),
          phone: formData.phone.trim(),
          is_active: formData.is_active,
          updated_at: new Date().toISOString(),
        };

        await updateDoc(customerRef, updateData);
        
        console.log('✅ [CUSTOMERS] Customer updated successfully:', selectedCustomer.id);
        
        setSnackbar({
          open: true,
          message: `Customer "${formData.first_name} ${formData.last_name}" updated successfully`,
          severity: 'success',
        });
      } else {
        // Create new customer
        if (!formData.password || formData.password.length < 6) {
          setSnackbar({
            open: true,
            message: 'Password must be at least 6 characters',
            severity: 'error',
          });
          return;
        }

        if (formData.password !== formData.confirmPassword) {
          setSnackbar({
            open: true,
            message: 'Passwords do not match',
            severity: 'error',
          });
          return;
        }

        // Create Firebase Auth user
        const normalizedEmail = formData.email.trim().toLowerCase();
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          normalizedEmail,
          formData.password
        );

        const authUser = userCredential.user;
        
        // Update display name (Firebase Auth doesn't have updateDisplayName method)
        // Display name will be set via the Firestore document

        // Create customer document in Firestore
        const customerDoc = {
          email: normalizedEmail,
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          full_name: `${formData.first_name.trim()} ${formData.last_name.trim()}`,
          phone: formData.phone.trim(),
          role: 'customer',
          is_active: formData.is_active,
          created_by: 'admin', // Mark as admin-created
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        await addDoc(collection(db, 'customers'), customerDoc);
        
        setSnackbar({
          open: true,
          message: `Customer "${formData.first_name} ${formData.last_name}" created successfully`,
          severity: 'success',
        });
      }

      setEditDialogOpen(false);
      setSelectedCustomer(null);
      await fetchCustomers();
      } catch (e: any) {
      console.error('❌ [CUSTOMERS] Error saving customer:', e);
      console.error('   Error code:', e.code);
      console.error('   Error message:', e.message);
      
      // Check if it's a permission error
      if (e.code === 'permission-denied' || e.message?.includes('permission')) {
        console.error('⚠️ [CUSTOMERS] Permission denied error detected!');
        console.error('   This usually means the user document in Firestore is missing or role is not "admin"');
        console.error('   Try running: await window.fixAdminRole() in browser console');
        
        // Try to fix the role automatically
        try {
          const { auth } = await import('../../services/firebaseConfig');
          const { doc, getDoc, setDoc } = await import('../../services/firestoreCompat');
          const { db } = await import('../../services/firebaseConfig');
          
          const user = auth.currentUser;
          if (user) {
            console.log('🔧 [CUSTOMERS] Attempting to fix admin role automatically...');
            const userDocRef = doc(db, 'users', user.uid);
            const userDocSnap = await getDoc(userDocRef);
            
            if (!userDocSnap.exists() || userDocSnap.data()?.role !== 'admin') {
              await setDoc(userDocRef, {
                uid: user.uid,
                email: user.email || '',
                role: 'admin',
                is_active: true,
                updated_at: new Date().toISOString(),
                permissions: ['read_orders', 'write_orders', 'manage_users', 'manage_settings', 'manage_customers'],
              }, { merge: true });
              console.log('✅ [CUSTOMERS] Admin role fixed! Please try saving again.');
              
              setSnackbar({
                open: true,
                message: 'Admin role fixed! Please try saving the customer again.',
                severity: 'success',
              });
              return;
            }
          }
        } catch (fixError: any) {
          console.error('❌ [CUSTOMERS] Failed to auto-fix role:', fixError);
        }
      }
      
      setSnackbar({
        open: true,
        message: `Failed to save customer: ${e.message || 'Unknown error'}. ${e.code === 'permission-denied' ? 'Try: await window.fixAdminRole() in console' : ''}`,
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (customer: Customer) => {
    setSelectedCustomer(customer);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedCustomer) return;

    try {
      setDeleting(true);

      // Step 1: Delete customer document from Firestore
      await deleteDoc(doc(db, 'customers', selectedCustomer.id));
      console.log(`✅ Deleted customer document: ${selectedCustomer.id}`);

      // Step 2: Delete Firebase Auth user (using Cloud Function)
      try {
        const { SessionManager } = await import('../../utils/sessionManager');
        const token = SessionManager.getAccessToken();
        
        if (token && selectedCustomer.id) {
          // The customer ID is the Firebase Auth UID
          const response = await fetch('__SAHEEH_FUNCTIONS_URL_UNSET__/deleteAuthUser', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ uid: selectedCustomer.id }),
          });

          if (response.ok) {
            console.log(`✅ Deleted Firebase Auth user: ${selectedCustomer.id}`);
          } else {
            const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
            console.warn(`⚠️  Could not delete Firebase Auth user: ${errorData.error || 'Unknown error'}`);
            // Continue anyway - Firestore document is deleted
          }
        } else {
          console.warn('⚠️  No auth token available, skipping Firebase Auth deletion');
          // Continue anyway - Firestore document is deleted
        }
      } catch (authError: any) {
        console.warn(`⚠️  Error deleting Firebase Auth user: ${authError.message}`);
        // Continue anyway - Firestore document is deleted
      }

      setSnackbar({
        open: true,
        message: `Customer "${selectedCustomer.full_name}" deleted successfully`,
        severity: 'success',
      });

      // Refresh list
      await fetchCustomers();

      setDeleteDialogOpen(false);
      setSelectedCustomer(null);
    } catch (e: any) {
      console.error('❌ Error deleting customer:', e);
      setSnackbar({
        open: true,
        message: `Failed to delete customer: ${e.message}`,
        severity: 'error',
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (customer: Customer) => {
    try {
      const customerRef = doc(db, 'customers', customer.id);
      const newActiveStatus = !(customer.is_active !== false);
      
      await updateDoc(customerRef, {
        is_active: newActiveStatus,
        updated_at: new Date().toISOString(),
      });

      setSnackbar({
        open: true,
        message: `Customer ${newActiveStatus ? 'activated' : 'deactivated'} successfully`,
        severity: 'success',
      });

      await fetchCustomers();
    } catch (e: any) {
      console.error('❌ Error toggling customer status:', e);
      setSnackbar({
        open: true,
        message: `Failed to update customer status: ${e.message}`,
        severity: 'error',
      });
    }
  };

  const filteredCustomers = customers.filter((customer) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      customer.email.toLowerCase().includes(searchLower) ||
      customer.full_name.toLowerCase().includes(searchLower) ||
      customer.phone.toLowerCase().includes(searchLower) ||
      customer.first_name.toLowerCase().includes(searchLower) ||
      customer.last_name.toLowerCase().includes(searchLower)
    );
  });

  const formatDate = (timestamp: Timestamp | null) => {
    if (!timestamp) return 'N/A';
    try {
      const date = typeof timestamp === 'string'
        ? new Date(timestamp)
        : (timestamp as any).toDate?.() ?? new Date(timestamp as any);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (e) {
      return 'N/A';
    }
  };

  const activeCustomers = customers.filter(c => c.is_active !== false).length;
  const inactiveCustomers = customers.filter(c => c.is_active === false).length;
  const totalOrders = customers.reduce((sum, c) => sum + (c.total_orders || 0), 0);
  const totalRevenue = customers.reduce((sum, c) => sum + (c.total_spent || 0), 0);
  const todayOrders = customers.filter(c => {
    if (!c.last_order_date) return false;
    try {
      const v: any = c.last_order_date;
      const lastOrderDate = typeof v === 'string' ? new Date(v) : (v.toDate?.() ?? new Date(v));
      const today = new Date();
      return lastOrderDate.toDateString() === today.toDateString();
    } catch (e) {
      return false;
    }
  }).reduce((sum, c) => sum + (c.total_orders || 0), 0);

  return (
    <AdminLayout>
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        {/* Header */}
        <Box sx={{ mb: { xs: 2, md: 3 }, display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, flexWrap: 'wrap', gap: 1.5 }}>
          <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 700, fontFamily: '"Poppins"', fontSize: { xs: '1.1rem', md: '1.5rem' } }}>
            {t('customers.title')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="contained" startIcon={<Add />} onClick={handleAddClick} size="small"
              sx={{ bgcolor: '#00C896', color: '#1a1d2e', '&:hover': { bgcolor: '#7bc912' }, fontFamily: '"Poppins"', fontWeight: 600, fontSize: { xs: '0.75rem', md: '0.85rem' }, px: { xs: 1.5, md: 2.5 } }}>
              {t('customers.addCustomer')}
            </Button>
            <IconButton onClick={fetchCustomers} disabled={loading} size="small"
              sx={{ border: '1px solid #3a3d52', borderRadius: '8px', color: '#9ca3af', width: 36, height: 36, '&:hover': { borderColor: '#00C896', color: '#00C896' } }}>
              <Refresh sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress sx={{ color: '#00C896' }} /></Box>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && !error && (
          <>
            {/* KPI Stats — Compact Grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: { xs: 1, md: 2 }, mb: { xs: 2, md: 3 } }}>
              {[
                { label: t('customers.totalCustomers'), value: customers.length, icon: <Person />, color: '#2196F3' },
                { label: t('customers.activeCustomers'), value: activeCustomers, icon: <CheckCircle />, color: '#4CAF50' },
                { label: t('customers.totalOrders'), value: totalOrders, icon: <ShoppingCart />, color: '#FF9800' },
                { label: t('customers.totalRevenue'), value: `€${totalRevenue.toFixed(2)}`, icon: <Euro />, color: '#9C27B0' },
              ].map((stat) => (
                <Box key={stat.label} sx={{
                  py: { xs: 1.5, md: 2 }, px: { xs: 1.5, md: 2 },
                  borderRadius: '10px', backgroundColor: '#252838', border: '1px solid #3a3d52',
                  borderTop: `3px solid ${stat.color}`,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Box sx={{ color: stat.color, '& .MuiSvgIcon-root': { fontSize: { xs: 18, md: 22 } } }}>{stat.icon}</Box>
                    <Typography sx={{ color: '#9ca3af', fontSize: { xs: '0.65rem', md: '0.75rem' }, fontFamily: '"Poppins"', fontWeight: 500 }}>
                      {stat.label}
                    </Typography>
                  </Box>
                  <Typography sx={{ color: '#ffffff', fontWeight: 700, fontSize: { xs: '1.1rem', md: '1.5rem' }, fontFamily: '"Poppins"', lineHeight: 1.2 }}>
                    {stat.value}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Search */}
            <TextField
              fullWidth
              placeholder={t('customers.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ mb: { xs: 2, md: 3 }, '& .MuiOutlinedInput-root': { borderRadius: '10px', height: { xs: 38, md: 42 } } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: '#6b7280', fontSize: 20 }} /></InputAdornment> }}
            />

            {/* Desktop: Table View */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Paper sx={{ backgroundColor: '#252838', border: '1px solid #3a3d52', borderRadius: '12px', overflow: 'hidden' }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#1a1d2e' }}>
                        {['Customer', 'Contact', 'Orders', 'Spent', 'Status', 'Joined', 'Actions'].map((h) => (
                          <TableCell key={h} sx={{ color: '#9ca3af', fontWeight: 600, fontFamily: '"Poppins"', fontSize: '0.78rem', borderBottom: '2px solid #3a3d52', py: 1.2 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredCustomers.length === 0 ? (
                        <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: '#6b7280' }}>{t('customers.noCustomers')}</TableCell></TableRow>
                      ) : filteredCustomers.map((customer) => (
                        <TableRow key={customer.id} sx={{ '&:hover': { backgroundColor: 'rgba(0, 200, 150,0.04)' } }}>
                          <TableCell sx={{ py: 1.2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar src={customer.profile_image_url} sx={{ width: 34, height: 34, bgcolor: '#00C896', color: '#1a1d2e', fontSize: '0.8rem', fontWeight: 700 }}>
                                {customer.full_name.charAt(0).toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography sx={{ color: '#e0e0e0', fontWeight: 600, fontSize: '0.85rem', fontFamily: '"Poppins"' }}>{customer.full_name}</Typography>
                                <Typography sx={{ color: '#6b7280', fontSize: '0.7rem', fontFamily: '"Poppins"' }}>ID: {customer.id.substring(0, 8)}...</Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 1.2 }}>
                            {customer.phone && <Typography sx={{ color: '#00C896', fontSize: '0.78rem', fontWeight: 600, fontFamily: '"Poppins"' }}>📱 {customer.phone}</Typography>}
                            {customer.email && <Typography sx={{ color: '#9ca3af', fontSize: '0.72rem', fontFamily: '"Poppins"' }}>✉ {customer.email}</Typography>}
                            {customer.street && <Typography sx={{ color: '#6b7280', fontSize: '0.68rem', fontFamily: '"Poppins"' }}>📍 {customer.street}, {customer.postcode} {customer.city}</Typography>}
                            {!customer.phone && !customer.email && <Typography sx={{ color: '#555', fontSize: '0.72rem' }}>—</Typography>}
                          </TableCell>
                          <TableCell sx={{ py: 1.2 }}>
                            <Typography sx={{ color: '#e0e0e0', fontWeight: 600, fontSize: '0.85rem', fontFamily: '"Poppins"' }}>{customer.total_orders || 0}</Typography>
                            {customer.last_order_date && <Typography sx={{ color: '#6b7280', fontSize: '0.65rem', fontFamily: '"Poppins"' }}>Last: {formatDate(customer.last_order_date)}</Typography>}
                          </TableCell>
                          <TableCell sx={{ color: '#00C896', fontWeight: 700, fontSize: '0.85rem', fontFamily: '"Poppins"', py: 1.2 }}>€{(customer.total_spent || 0).toFixed(2)}</TableCell>
                          <TableCell sx={{ py: 1.2 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                              <Chip label={customer.source === 'otp' ? '📱 OTP' : customer.source === 'webshop' ? '🌐 Web' : customer.source === 'email' ? '✉ Email' : customer.source || '—'} size="small"
                                sx={{ bgcolor: customer.source === 'otp' ? 'rgba(0, 200, 150,0.12)' : 'rgba(33,150,243,0.12)', color: customer.source === 'otp' ? '#00C896' : '#64B5F6', fontWeight: 600, fontSize: '0.65rem', height: 20, border: `1px solid ${customer.source === 'otp' ? '#00C89633' : '#64B5F633'}` }} />
                              <Chip label={customer.is_active !== false ? 'Active' : 'Inactive'} size="small"
                                sx={{ bgcolor: customer.is_active !== false ? 'rgba(76,175,80,0.15)' : 'rgba(158,158,158,0.15)', color: customer.is_active !== false ? '#4CAF50' : '#999', fontWeight: 600, fontSize: '0.65rem', height: 20, border: `1px solid ${customer.is_active !== false ? '#4CAF5044' : '#99999944'}` }} />
                            </Box>
                          </TableCell>
                          <TableCell sx={{ color: '#9ca3af', fontSize: '0.75rem', fontFamily: '"Poppins"', py: 1.2 }}>{formatDate(customer.created_at)}</TableCell>
                          <TableCell sx={{ py: 1.2 }}>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton size="small" onClick={() => handleEditClick(customer)} sx={{ color: '#2196F3', width: 28, height: 28 }}><Edit sx={{ fontSize: 16 }} /></IconButton>
                              <IconButton size="small" onClick={() => handleToggleActive(customer)} sx={{ color: customer.is_active !== false ? '#FF9800' : '#4CAF50', width: 28, height: 28 }}>
                                {customer.is_active !== false ? <Block sx={{ fontSize: 16 }} /> : <CheckCircle sx={{ fontSize: 16 }} />}
                              </IconButton>
                              <IconButton size="small" onClick={() => handleDeleteClick(customer)} sx={{ color: '#F44336', width: 28, height: 28 }}><Delete sx={{ fontSize: 16 }} /></IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>

            {/* Mobile: Card View */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5 }}>
              {filteredCustomers.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4, color: '#6b7280' }}>{t('customers.noCustomers')}</Box>
              ) : filteredCustomers.map((customer) => (
                <Box key={customer.id} sx={{
                  backgroundColor: '#252838', border: '1px solid #3a3d52', borderRadius: '10px',
                  p: 1.5, display: 'flex', flexDirection: 'column', gap: 1,
                }}>
                  {/* Top: Avatar + Name + Actions */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar src={customer.profile_image_url} sx={{ width: 38, height: 38, bgcolor: '#00C896', color: '#1a1d2e', fontSize: '0.85rem', fontWeight: 700 }}>
                      {customer.full_name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ color: '#ffffff', fontWeight: 600, fontSize: '0.88rem', fontFamily: '"Poppins"', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {customer.full_name}
                      </Typography>
                      <Typography sx={{ color: '#6b7280', fontSize: '0.7rem', fontFamily: '"Poppins"', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {customer.email}
                      </Typography>
                    </Box>
                    <Chip label={customer.is_active !== false ? 'Active' : 'Off'} size="small"
                      sx={{ bgcolor: customer.is_active !== false ? 'rgba(76,175,80,0.15)' : 'rgba(158,158,158,0.15)', color: customer.is_active !== false ? '#4CAF50' : '#999', fontWeight: 600, fontSize: '0.6rem', height: 20 }} />
                  </Box>
                  {/* Stats Row */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Box sx={{ flex: 1, py: 0.8, px: 1, borderRadius: '6px', backgroundColor: '#1e2139', textAlign: 'center' }}>
                      <Typography sx={{ color: '#9ca3af', fontSize: '0.58rem', fontFamily: '"Poppins"', textTransform: 'uppercase' }}>Orders</Typography>
                      <Typography sx={{ color: '#e0e0e0', fontWeight: 700, fontSize: '0.9rem', fontFamily: '"Poppins"' }}>{customer.total_orders || 0}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, py: 0.8, px: 1, borderRadius: '6px', backgroundColor: '#1e2139', textAlign: 'center' }}>
                      <Typography sx={{ color: '#9ca3af', fontSize: '0.58rem', fontFamily: '"Poppins"', textTransform: 'uppercase' }}>Spent</Typography>
                      <Typography sx={{ color: '#00C896', fontWeight: 700, fontSize: '0.9rem', fontFamily: '"Poppins"' }}>€{(customer.total_spent || 0).toFixed(2)}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, py: 0.8, px: 1, borderRadius: '6px', backgroundColor: '#1e2139', textAlign: 'center' }}>
                      <Typography sx={{ color: '#9ca3af', fontSize: '0.58rem', fontFamily: '"Poppins"', textTransform: 'uppercase' }}>Joined</Typography>
                      <Typography sx={{ color: '#e0e0e0', fontWeight: 600, fontSize: '0.7rem', fontFamily: '"Poppins"' }}>{formatDate(customer.created_at)}</Typography>
                    </Box>
                  </Box>
                  {/* Actions */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, pt: 0.5, borderTop: '1px solid #2a2d3e' }}>
                    <IconButton size="small" onClick={() => handleEditClick(customer)} sx={{ color: '#2196F3', width: 30, height: 30 }}><Edit sx={{ fontSize: 16 }} /></IconButton>
                    <IconButton size="small" onClick={() => handleToggleActive(customer)} sx={{ color: customer.is_active !== false ? '#FF9800' : '#4CAF50', width: 30, height: 30 }}>
                      {customer.is_active !== false ? <Block sx={{ fontSize: 16 }} /> : <CheckCircle sx={{ fontSize: 16 }} />}
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteClick(customer)} sx={{ color: '#F44336', width: 30, height: 30 }}><Delete sx={{ fontSize: 16 }} /></IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}

        {/* Edit/Add Dialog */}
        <Dialog 
          open={editDialogOpen} 
          onClose={() => !saving && setEditDialogOpen(false)}
          maxWidth="md" fullWidth
        >
          <DialogTitle>
            <Typography variant="h6">
              {selectedCustomer ? t('customers.editCustomer') : t('customers.addNewCustomer')}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('customers.firstName')}
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('customers.lastName')}
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('customers.email')}
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={!!selectedCustomer}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('customers.phone')}
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </Grid>
              {!selectedCustomer && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('customers.password')}
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label={t('customers.confirmPassword')}
                      type={showPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      required
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t('common.status')}</InputLabel>
                  <Select
                    value={formData.is_active ? 'active' : 'inactive'}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
                    label={t('common.status')}
                  >
                    <MenuItem value="active">{t('common.active')}</MenuItem>
                    <MenuItem value="inactive">{t('common.inactive')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSaveCustomer}
              color="primary"
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : null}
            >
              {saving ? t('common.saving') : selectedCustomer ? t('common.update') : t('common.create')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
          <DialogTitle>
            <Typography variant="h6" color="error">
              {t('customers.deleteCustomerAccount')}
            </Typography>
          </DialogTitle>
          <DialogContent>
            {selectedCustomer && (
              <Box>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {t('customers.deleteWarning')}
                </Alert>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>{t('customers.customer')}:</strong> {selectedCustomer.full_name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>{t('customers.email')}:</strong> {selectedCustomer.email}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  <strong>{t('customers.phone')}:</strong> {selectedCustomer.phone || 'N/A'}
                </Typography>
                <Typography variant="body2" color="error">
                  {t('customers.deleteDisclaimer')}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={16} /> : <Delete />}
            >
              {deleting ? t('common.deleting') : t('customers.deleteCustomer')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        />
      </Box>
    </AdminLayout>
  );
};

export default CustomersPage;

